import React, { useState } from 'react';
import { FileText, Building2, Building, Briefcase } from 'lucide-react';
import SEOHead from '../components/SEO/SEOHead';
import ExcelViewer from '../components/ExcelViewer';

function SampleReportPage() {
  const [selectedReport, setSelectedReport] = useState('patent');
  const [companyType, setCompanyType] = useState('public');
  const [patentReportType, setPatentReportType] = useState('detailed');

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEOHead 
        title="Sample Patent Valuation Reports | IP Analytics Examples"
        description="View sample patent valuation reports and company IP analyses. See real examples of our AI-powered patent assessments, licensing insights, and investment analysis reports."
        keywords="sample patent valuation reports, patent report examples, IP analytics samples, patent assessment examples, company valuation reports, intellectual property reports"
        ogTitle="Sample Patent Valuation Reports - See Real Examples"
        ogDescription="Explore comprehensive sample reports showing our patent valuation methodology, IP analytics, and investment insights."
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Sample Patent Valuation Reports",
          "description": "Collection of sample patent valuation and company analysis reports",
          "hasPart": [
            {
              "@type": "DigitalDocument",
              "name": "Patent Valuation Report Sample",
              "description": "Sample patent valuation report showing AI-powered patent assessment methodology",
              "encodingFormat": "application/pdf"
            },
            {
              "@type": "DigitalDocument", 
              "name": "Public Company Valuation Report Sample",
              "description": "Sample company valuation report for public companies with patent portfolios",
              "encodingFormat": "application/pdf"
            },
            {
              "@type": "DigitalDocument",
              "name": "Private Company Valuation Report Sample", 
              "description": "Sample company valuation report for private companies with IP assets",
              "encodingFormat": "application/pdf"
            }
          ]
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Sample Valuation Reports
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            View our sample reports to see the depth and quality of our analysis
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Report Type Selection */}
          <div className="mb-8">
            <div className="text-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Report Type</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <button
                  onClick={() => setSelectedReport('patent')}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                    selectedReport === 'patent'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      selectedReport === 'patent'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <FileText className="h-6 w-6" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium text-gray-900">Patent Report</h3>
                      <p className="text-sm text-gray-500">Detailed patent valuation analysis</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setSelectedReport('company')}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                    selectedReport === 'company'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      selectedReport === 'company'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Building2 className="h-6 w-6" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium text-gray-900">Company Report</h3>
                      <p className="text-sm text-gray-500">Comprehensive company valuation</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Patent Report Type Selection (only shown when patent report is selected) */}
            {selectedReport === 'patent' && (
              <div className="text-center">
                <div className="inline-block h-0.5 w-16 bg-gray-200 mb-4"></div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Report Type</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  <button
                    onClick={() => setPatentReportType('detailed')}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                      patentReportType === 'detailed'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${
                        patentReportType === 'detailed'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <FileText className="h-6 w-6" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">Detailed Report</h3>
                        <p className="text-sm text-gray-500">Individual patent analysis</p>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setPatentReportType('portfolio')}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                      patentReportType === 'portfolio'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${
                        patentReportType === 'portfolio'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Briefcase className="h-6 w-6" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">Portfolio Report</h3>
                        <p className="text-sm text-gray-500">Portfolio-level analysis</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Company Type Selection (only shown when company report is selected) */}
            {selectedReport === 'company' && (
              <div className="text-center">
                <div className="inline-block h-0.5 w-16 bg-gray-200 mb-4"></div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Company Type</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  <button
                    onClick={() => setCompanyType('public')}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                      companyType === 'public'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${
                        companyType === 'public'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Building className="h-6 w-6" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">Public Company</h3>
                        <p className="text-sm text-gray-500">Listed company analysis</p>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setCompanyType('private')}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                      companyType === 'private'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${
                        companyType === 'private'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Briefcase className="h-6 w-6" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">Private Company</h3>
                        <p className="text-sm text-gray-500">Private company valuation</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Report Viewer */}
          <div className="w-full">
            {selectedReport === 'patent' && patentReportType === 'portfolio' ? (
              // Excel Portfolio Report Viewer
              <ExcelViewer 
                filePath="/sample-portfolio-report.xlsx" 
                fileName="Portfolio Report"
              />
            ) : (
              // PDF Viewer for Detailed Patent Report and Company Reports
              <div className="flex justify-center">
                <div className="w-full max-w-4xl">
                  <iframe
                    src={
                      selectedReport === 'patent'
                        ? '/sample-patent-report.pdf'
                        : companyType === 'public'
                        ? '/sample-public-company-report.pdf'
                        : '/sample-private-company-report.pdf'
                    }
                    className="w-full h-[800px] border border-gray-200 rounded-lg"
                    title="Sample Valuation Report"
                  >
                    <p className="text-center text-gray-500">
                      Your browser does not support PDFs. Please download the PDF to view it: 
                      <a
                        href={
                          selectedReport === 'patent'
                            ? '/sample-patent-report.pdf'
                            : companyType === 'public'
                            ? '/sample-public-company-report.pdf'
                            : '/sample-private-company-report.pdf'
                        }
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Download PDF
                      </a>
                    </p>
                  </iframe>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SampleReportPage; 